
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { logger } from "@/services/LoggingService";
import { Fund } from "@/hooks/useFundData";
import { useUserRole } from "@/hooks/useUserRole";

interface Contact {
  id: string;
  full_name: string | null;
  title: string | null;
  work_email: string | null;
  linkedin_profile: string | null;
  phone_number: string | null;
  key_contact: boolean;
  avatar_url: string | null;
}

interface ContactPhotoMutationContext {
  previousData: Fund | undefined;
  queryKey: readonly unknown[];
}

const COMPONENT = 'ContactPhotoMutation';

export function useContactPhotoMutation(contactId: string, fundId: string) {
  const queryClient = useQueryClient();
  const queryKey = ['fund', fundId, 'contacts', contactId] as const;
  const { userRole } = useUserRole();

  return useMutation({
    mutationFn: async (avatarUrl: string | null) => {
      logger.info(COMPONENT, 'Attempting photo update', {
        operation: 'updatePhoto',
        context: {
          contactId,
          avatarUrl,
          userRole,
          timestamp: new Date().toISOString()
        }
      });

      if (!contactId) throw new Error("No contact ID provided");

      // Check if user has permission to update photos
      if (userRole !== 'sys_admin' && userRole !== 'manager') {
        const error = new Error("You don't have permission to update contact photos");
        logger.error(COMPONENT, 'Permission denied', {
          userRole,
          contactId,
          timestamp: new Date().toISOString()
        });
        throw error;
      }

      const { data, error } = await supabase
        .from('fund_contacts')
        .update({ avatar_url: avatarUrl })
        .eq('id', contactId)
        .select()
        .single();

      if (error) {
        logger.error(COMPONENT, 'Operation failed', {
          error: error.message,
          details: error,
          context: {
            contactId,
            avatarUrl,
            userRole,
            timestamp: new Date().toISOString()
          }
        });
        throw error;
      }

      return data;
    },
    onMutate: async (newAvatarUrl) => {
      await queryClient.cancelQueries({ queryKey });
      const previousData = queryClient.getQueryData<Fund>(['fund', fundId]);

      // Optimistically update the fund data
      queryClient.setQueryData<Fund>(['fund', fundId], (old) => {
        if (!old?.company?.fund_contacts) return old;
        return {
          ...old,
          company: {
            ...old.company,
            fund_contacts: old.company.fund_contacts.map((contact) =>
              contact.id === contactId
                ? { ...contact, avatar_url: newAvatarUrl }
                : contact
            )
          }
        };
      });

      return { previousData, queryKey } as ContactPhotoMutationContext;
    },
    onError: (error, _, context) => {
      logger.error(COMPONENT, 'Operation failed', {
        error: error.message,
        context: {
          contactId,
          timestamp: new Date().toISOString()
        }
      });

      if (context) {
        queryClient.setQueryData(context.queryKey, context.previousData);
      }
      toast.error(error.message || "Failed to update contact photo");
    },
    onSuccess: (data) => {
      toast.success("Contact photo updated successfully");
      queryClient.invalidateQueries({ queryKey });
    }
  });
}
